import { createRouter, createWebHistory } from 'vue-router'
import { PRIORITIES } from '@/core/constants/installation-priorities.constant'
import { createAuthGuardPlugin } from '@/core/guards/auth.guard'
import { useBreadcrumbStore } from '@/shared/stores/breadcrumb.store.js'

export const priority = PRIORITIES.ROUTER

/**
 * Fallback route for 404 errors
 */
const fallbackRoute = {
  path: '/:pathMatch(.*)*',
  name: 'NotFound',
  component: () => import('@/core/components/dss-errors/dss-404.vue'),
  meta: {
    layout: 'error',
  },
}

const router = createAppRouter()

/**
 * Creates the main application router with all configured routes
 * @returns {import('vue-router').Router} Configured Vue Router instance
 */
function createAppRouter() {
  try {
    const router = createRouter({
      history: createWebHistory(),
      routes: createRouteConfig(),
    })

    configureMiddleware(router)
    configureBreadcrumbs(router)

    return router
  }
  catch (error) {
    console.error('Failed to create router:', error)
    // Create a minimal router that works with error routes to prevent app crash
    return createRouter({
      history: createWebHistory(),
      routes: [fallbackRoute],
    })
  }
}

/**
 * Creates the complete route configuration by combining module routes
 * @returns {Array} Combined routes array
 */
function createRouteConfig() {
  try {
    const moduleRoutes = loadModulesRoutes()
    return [...moduleRoutes, fallbackRoute]
  }
  catch (error) {
    console.error('Failed to create route config:', error)
    return [fallbackRoute]
  }
}

/**
 * Loads all route modules from the application
 * @returns {Array} Combined array of route objects
 */
function loadModulesRoutes() {
  try {
    const modules = import.meta.glob('@/modules/**/routes/index.js', {
      eager: true,
    })

    return Object.values(modules).flatMap((module) => {
      const routes = module.default || []
      if (!Array.isArray(routes)) {
        console.warn('Module did not return an array of routes:', module)
        return []
      }
      return routes.map(enhanceRoute)
    })
  }
  catch (error) {
    console.error('Failed to load module routes:', error)
    return []
  }
}

/**
 * Enhances route with additional metadata and validation
 * @param {object} route - Route configuration object
 * @returns {object} Enhanced route object
 */
function enhanceRoute(route) {
  if (!route.path) {
    console.warn('Route missing path:', route)
  }

  if (!route.component && !route.children) {
    console.warn('Route missing component or children:', route)
  }

  return {
    ...route,
    meta: {
      ...route.meta,
      // Add any global route metadata here
    },
  }
}

/**
 * Configures router middleware like authentication guards
 * @param {import('vue-router').Router} router - Vue Router instance
 */
function configureMiddleware(router) {
  try {
    const authPlugin = createAuthGuardPlugin()
    router.beforeEach(authPlugin.beforeEach)
  }
  catch (error) {
    console.error('Failed to configure router middleware:', error)
  }
}

/**
 * Configures breadcrumb generation based on route metadata
 * @param {import('vue-router').Router} router - Vue Router instance
 */
function configureBreadcrumbs(router) {
  router.afterEach((to) => {
    try {
      const breadcrumbStore = useBreadcrumbStore()
      const matched = to.matched

      if (!matched || matched.length === 0) {
        breadcrumbStore.setBreadcrumb(null)
        return
      }

      const breadcrumbs = []

      matched.forEach((route, index) => {
        if (route.meta?.breadcrumb) {
          const { label, route: breadcrumbRoute } = route.meta.breadcrumb
          const isFirst = index === 0
          const isLast = index === matched.length - 1

          const processedLabel
            = typeof label === 'function' ? label(to.params, to.query) : label

          const crumb = {
            label: processedLabel,
          }

          // Add route for middle items (not first or last)
          if (!isFirst && !isLast) {
            crumb.route = breadcrumbRoute || route.name
          }

          // Add icon if specified
          if (route.meta.breadcrumb.icon) {
            crumb.icon = route.meta.breadcrumb.icon
          }

          breadcrumbs.push(crumb)
        }
      })

      breadcrumbStore.setBreadcrumb(breadcrumbs)
    }
    catch (error) {
      console.error('Failed to update breadcrumbs:', error)
      // Avoid crashing the navigation if breadcrumbs fail
      useBreadcrumbStore().setBreadcrumb(null)
    }
  })
}

export default router

/**
 * Installs the router into the Vue application
 * @param {object} param0 - Installation context
 * @param {import('vue').App} param0.app - Vue application instance
 */
export function install({ app }) {
  app.use(router)
}
